import { Gesture } from "@-/com.npmjs.react-native-gesture-handler";
import { AppState, Platform } from "@-/com.npmjs.react-native";
import {
  cancelAnimation,
  runOnJS,
  runOnUI,
  useSharedValue,
  withSpring,
} from "@-/com.npmjs.react-native-reanimated";
import useLatest from "@-/com.npmjs.use-latest";
import {
  clampDragWithFriction,
  LEARN_DECK_RECOVERY_SPRING,
  isHorizontalDragCommitted,
  HORIZONTAL_DRAG_OVERSHOOT,
  horizontalDragFreeDistance,
  rawDragForVisualOffset,
  tiltForHorizontalOffset,
} from "@-/dev.improve.haniverse.lib.learn.deck";
import { isSlideLearnCardPresentationKey } from "@-/dev.improve.haniverse.lib.learn.card-action";
import type {
  LearnCardSlidePages,
  LearnDeckStageValues,
} from "@-/dev.improve.haniverse.routes.nav.app.learn.deck.context";
import { useEffect, useMemo, useState } from "react";

import { LEARN_DECK_PARK_SPRING } from "#learn-deck-springs";
import { exitSlideView, slideViewPosition } from "#learn-card-slide-view-motion";
import {
  learnCardParkPose,
  pagedSuccessorOffset,
  releaseLearnCard,
} from "#learn-card-release-motion";
import {
  isInLearnFlashcardHorizontalGestureBlocker,
  type LearnFlashcardHorizontalGestureBlockerViews,
} from "#use-learn-card-horizontal-gesture-blockers";

/** Horizontal travel before the pan wins over taps and vertical scrolling underneath. */
const HORIZONTAL_ACTIVATION_PX = 4;
/** Vertical travel that fails the pan, leaving the drag to the card's own scroll view. */
const VERTICAL_FAIL_PX = 12;

const IS_WEB = Platform.OS === "web";

/**
 * What a drag moves: the card; the top view of a slide card; or a slide card itself, which pages on
 * to the next flashcard (untilted, with the finger, and only to the left) instead of being swiped.
 */
const DRAG_TARGET_CARD = 0;
const DRAG_TARGET_VIEW = 1;
const DRAG_TARGET_PAGE = 2;

/**
 * On web, a mouse press on selectable slide text starts a browser selection that would otherwise
 * stretch along with the swipe, so the swipe keeps it cleared. Native selection needs a
 * stationary long press, which a swipe never is.
 */
function clearWebTextSelection() {
  const selection = globalThis.document?.getSelection();
  if (selection != null && !selection.isCollapsed) {
    selection.removeAllRanges();
  }
}

/**
 * The horizontal drag on the card on show.
 *
 * Geometry runs on the UI thread: the card follows the finger until its corner extends 9pt beyond the
 * screen edge, then strongly resists with capped travel and tilt. A release uses the same 9pt overflow
 * threshold as the footer pressed cue. Only two events cross to React:
 * the drag beginning (revealing a front-facing card) and its outcome — a commit with a direction,
 * or a cancel after the card has sprung home.
 *
 * Touch ownership is the UI thread's, never React's: a touch drags whichever card
 * `stage.motionPresentation` has on show, and every callback names that card. After a release
 * that is the uncovered successor, which takes the next touch before React has promoted it (or
 * registered its controls); React then commits it, or holds its commit until it is current. A
 * committing release does what `stage.releaseMode` says (fly, park or return home), and parks
 * while an earlier released card is still waiting for its exit. `presentationKey` (React's current
 * card) only scopes the traits React supplies about that card: swipe-back, slide views and the
 * drag reveal.
 *
 * While `onSwipeBack` is set (the card is showing an in-card exploration, or the Learn summary's
 * daily-limit card offers Upgrade to Premium), the same release presses that instead: the card
 * springs home without revealing, parking or committing anything.
 *
 * A slide card never tilts and is never swiped off its successor. With `pages`, horizontal swipes
 * move through the slideshow's neighboring slides, untilted and sharing the card's release
 * threshold: a rightward swipe returns a slide, a leftward swipe advances. Past the last slide (or
 * on a slide of one view), a leftward swipe pages the whole card on: it follows the finger to the
 * left while the next flashcard, waiting beside it, follows in from the right edge. Nothing moves
 * for a rightward swipe on the first slide: there is nothing before it.
 *
 * Before scroll opt-in, vertical drift does not reject a card swipe. Once opted in,
 * `failOffsetY` leaves vertical movement to the content, including at a scroll boundary.
 */
export function useLearnCardHorizontalGesture({
  allowVerticalScroll = true,
  blockerViews,
  isCommitted = false,
  isEnabled,
  onCommit,
  onDragCancel,
  onDragStart,
  onPark,
  onSwipeBack,
  pages = null,
  presentationKey,
  revealOnDragStart = false,
  stage,
}: {
  allowVerticalScroll?: boolean;
  /** In-card controls' views (`useLearnFlashcardHorizontalGestureBlockers`) that touches can't drag from. */
  blockerViews?: LearnFlashcardHorizontalGestureBlockerViews;
  /**
   * The card's action is committed and it waits parked: a re-grab returns it to the edge. The deck
   * marks this on the UI thread instead (`stage.committedPresentation`).
   */
  isCommitted?: boolean;
  isEnabled: boolean;
  onCommit: (direction: "left" | "right", presentationKey: string, gestureId: number) => void;
  onDragCancel: (presentationKey: string) => void;
  onDragStart: (presentationKey: string, motionEpoch: number) => void;
  onPark: (direction: "left" | "right", presentationKey: string) => void;
  /** Replaces the commit: a release past the threshold returns the card home and calls this. */
  onSwipeBack?: () => void;
  /** The current slide card's views, whose top view a drag moves until the last one. */
  pages?: LearnCardSlidePages | null;
  presentationKey: string;
  /** Reveal a review front immediately, even while React is busy handling the drag. */
  revealOnDragStart?: boolean;
  stage: LearnDeckStageValues;
}) {
  const onCommitRef = useLatest(onCommit);
  const onDragCancelRef = useLatest(onDragCancel);
  const onDragStartRef = useLatest(onDragStart);
  const onParkRef = useLatest(onPark);
  const onSwipeBackRef = useLatest(onSwipeBack);
  const onSwipeToPageRef = useLatest(pages?.onSwipeToPage);
  const isSwipeBack = onSwipeBack !== undefined;
  const startOffset = useSharedValue(0);
  const freeDistance = useSharedValue(0);
  const hasDragged = useSharedValue(false);
  const activeGestureId = useSharedValue(0);
  /** The card the touch under way drags, fixed at touch-down. */
  const activeOwner = useSharedValue("");
  const dragTarget = useSharedValue(DRAG_TARGET_CARD);
  /** Whether this touch reported its drag start (a slide card reports it once it pages). */
  const hasReportedDragStart = useSharedValue(false);
  /**
   * Where the touch picked up a slide card that was off rest (paging in, parked or springing
   * home), which it then drags alone, never further right; `null` when the touch turns its views.
   */
  const pageGrabX = useSharedValue<number | null>(null);
  const pageCount = pages?.count ?? 0;
  const views = pages?.views ?? null;
  const [isAppActive, setIsAppActive] = useState(
    AppState.currentState !== "background" && AppState.currentState !== "inactive",
  );
  const acceptsTouches = useSharedValue(isEnabled && isAppActive);

  const { gesture, interrupt } = useMemo(() => {
    const {
      cardHeight,
      cardWidth,
      committedPresentation,
      dragX,
      dragY,
      exitingOperation,
      gestureId,
      isDragging,
      motionPresentation,
      release,
      releaseMode,
      stalledRelease,
      tilt,
    } = stage;
    const park = (direction: "left" | "right", owner: string) => {
      onParkRef.current(direction, owner);
    };
    /** Whether `owner`'s action is committed, so a release returns it to its parked edge. */
    const isOwnerCommitted = (owner: string) => {
      "worklet";
      return committedPresentation.get() === owner || (isCommitted && owner === presentationKey);
    };
    /** Whether the touch under way still owns the card it started on. */
    const ownsTouch = () => {
      "worklet";
      return (
        motionPresentation.get().current === activeOwner.get() &&
        activeGestureId.get() === gestureId.get()
      );
    };
    const snapToEdge = (direction: "left" | "right", owner: string, reportPark = true) => {
      "worklet";
      const target = learnCardParkPose(stage, owner, direction);
      // A card stalled at the snap point flies off toward the edge it was last left at.
      const stalled = stalledRelease.get();
      if (stalled?.presentation === owner) {
        stalledRelease.set({ direction, presentation: owner });
      }
      dragX.set(withSpring(target.x, LEARN_DECK_PARK_SPRING));
      tilt.set(withSpring(target.tilt, LEARN_DECK_PARK_SPRING));
      // Release is a touch event, never a decorative spring's completion event.
      if (reportPark) {
        runOnJS(park)(direction, owner);
      }
    };
    const springHome = () => {
      "worklet";
      dragX.set(withSpring(0, LEARN_DECK_RECOVERY_SPRING));
      dragY.set(withSpring(0, LEARN_DECK_RECOVERY_SPRING));
      tilt.set(withSpring(0, LEARN_DECK_RECOVERY_SPRING));
    };
    const commit = (direction: "left" | "right", owner: string, id: number) => {
      onCommitRef.current(direction, owner, id);
    };
    const cancel = (owner: string) => {
      onDragCancelRef.current(owner);
    };
    const swipeBack = () => {
      onSwipeBackRef.current?.();
    };
    const start = (owner: string, motionEpoch: number) => {
      // Revealing survives touch cancellation and a subsequent touch. The controller checks
      // the presentation generation, so Undo/promotion still rejects a stale queued reveal.
      onDragStartRef.current(owner, motionEpoch);
    };
    const swipeToPage = (page: number) => {
      onSwipeToPageRef.current?.(page);
    };
    const restView = () => {
      "worklet";
      views?.offsetX.set(withSpring(0, LEARN_DECK_RECOVERY_SPRING));
    };
    /**
     * Move a slide card under the finger, `offset` from where the touch began. Until its last view
     * the top view follows (a rightward drag on the first view stays put); past it the card pages,
     * its successor following in from the right. A drag reversing over the last view hands back.
     */
    const dragSlide = (offset: number) => {
      "worklet";
      const owner = activeOwner.get();
      const grabX = pageGrabX.get();
      const turnsViews = views !== null && grabX === null;
      if (turnsViews && (views.shown.get() < pageCount - 1 || offset >= 0)) {
        dragTarget.set(DRAG_TARGET_VIEW);
        dragX.set(0);
        const shown = views.shown.get();
        const incoming = Math.max(0, Math.min(pageCount - 1, shown + (offset < 0 ? 1 : -1)));
        views.incoming.set(incoming);
        views.offsetX.set(incoming === shown ? 0 : offset);
        return;
      }
      dragTarget.set(DRAG_TARGET_PAGE);
      if (turnsViews) {
        views.offsetX.set(0);
        views.incoming.set(views.shown.get());
      }
      const stride = pagedSuccessorOffset(stage, owner) ?? 0;
      const pageX = Math.max(-stride, Math.min(Math.max(0, grabX ?? 0), offset));
      dragX.set(pageX);
      tilt.set(0);
      if (pageX < 0 && !hasReportedDragStart.get()) {
        hasReportedDragStart.set(true);
        runOnJS(start)(owner, stage.motionEpoch.get());
      }
    };
    const interrupt = () => {
      "worklet";
      if (!ownsTouch() || !isDragging.get()) {
        return;
      }
      // Do not depend on RNGH delivering finalize when the OS takes away the touch.
      // Retire it first so late update/end/finalize events cannot rate or move the card.
      const owner = activeOwner.get();
      const wasDragged = hasDragged.get();
      gestureId.set(gestureId.get() + 1);
      hasDragged.set(false);
      isDragging.set(false);
      if (dragTarget.get() === DRAG_TARGET_VIEW) {
        restView();
        // Only a slide card that paged before the finger brought it back started a card drag.
        if (hasReportedDragStart.get()) {
          runOnJS(cancel)(owner);
        }
        return;
      }
      if (exitingOperation.get() !== null) {
        return;
      }
      if (isOwnerCommitted(owner)) {
        if (wasDragged) {
          snapToEdge(dragX.get() < 0 ? "left" : "right", owner);
        }
      } else {
        springHome();
        runOnJS(cancel)(owner);
      }
    };
    const gesture = Gesture.Pan()
      .enabled(isEnabled && isAppActive)
      .activeOffsetX([-HORIZONTAL_ACTIVATION_PX, HORIZONTAL_ACTIVATION_PX])
      .onTouchesCancelled(interrupt)
      .onTouchesDown((event, manager) => {
        const touch = event.allTouches[0];
        // A touch on an in-card control (the pinyin keyboard) belongs to it, even once it drifts.
        if (
          blockerViews !== undefined &&
          touch !== undefined &&
          isInLearnFlashcardHorizontalGestureBlocker(blockerViews, touch.absoluteX, touch.absoluteY)
        ) {
          manager.fail();
        }
      })
      .onBegin(() => {
        hasDragged.set(false);
        hasReportedDragStart.set(false);
        dragTarget.set(DRAG_TARGET_CARD);
        activeGestureId.set(-1);
        const owner = motionPresentation.get().current;
        if (!acceptsTouches.get() || exitingOperation.get() !== null) {
          return;
        }
        // Hold ownership from touch-down, including the recognition threshold and vertical
        // scroll/tap arbitration. Finalize releases it even when this pan never activates.
        activeOwner.set(owner);
        isDragging.set(true);
        gestureId.set(gestureId.get() + 1);
        activeGestureId.set(gestureId.get());
      })
      .onStart(() => {
        if (!acceptsTouches.get() || !ownsTouch() || exitingOperation.get() !== null) {
          return;
        }
        const owner = activeOwner.get();
        // React's traits describe its current card; a card on show ahead of its promotion has none.
        const isReactCurrent = owner === presentationKey;
        hasDragged.set(true);
        freeDistance.set(horizontalDragFreeDistance(cardWidth.get(), cardHeight.get()));
        startOffset.set(
          rawDragForVisualOffset({
            freeDistance: freeDistance.get(),
            maxDistance: freeDistance.get() + HORIZONTAL_DRAG_OVERSHOOT,
            translation: dragX.get(),
          }),
        );
        cancelAnimation(dragX);
        cancelAnimation(dragY);
        cancelAnimation(tilt);
        if (IS_WEB) {
          runOnJS(clearWebTextSelection)();
        }
        // Swiping back from an exploration never reveals or prepares the card underneath it.
        if (isSwipeBack && isReactCurrent) {
          return;
        }
        if (isSlideLearnCardPresentationKey(owner)) {
          // A slide card moves with the finger, untilted. Grabbing it off rest (paging in,
          // parked or on its way home) picks it up where it is; nothing is prepared or committed
          // until it pages. React's current, uncommitted slide at rest turns its views first.
          dragTarget.set(DRAG_TARGET_PAGE);
          startOffset.set(dragX.get());
          pageGrabX.set(dragX.get());
          if (
            views !== null &&
            isReactCurrent &&
            !isOwnerCommitted(owner) &&
            pageCount > 1 &&
            dragX.get() === 0
          ) {
            pageGrabX.set(null);
            dragTarget.set(DRAG_TARGET_VIEW);
            const offset = (views.shown.get() - slideViewPosition(views)) * views.width.get();
            cancelAnimation(views.leavingProgress);
            views.leaving.set(null);
            views.leavingProgress.set(0);
            views.incoming.set(views.shown.get());
            cancelAnimation(views.offsetX);
            views.offsetX.set(offset);
            // Grabbing a view on its way home picks it up where it is.
            startOffset.set(views.offsetX.get());
          }
          return;
        }
        if (revealOnDragStart && isReactCurrent) {
          stage.flipProgress.set(1);
        }
        runOnJS(start)(owner, stage.motionEpoch.get());
      })
      .onUpdate((event) => {
        if (!hasDragged.get() || !ownsTouch() || exitingOperation.get() !== null) {
          return;
        }
        if (IS_WEB) {
          runOnJS(clearWebTextSelection)();
        }
        if (dragTarget.get() !== DRAG_TARGET_CARD) {
          // Only a forward swipe past the last slide pages the flashcard on. A backward swipe
          // there returns to the previous slide, just as the visible left edge promises.
          dragSlide(startOffset.get() + event.translationX);
          return;
        }
        const shown = clampDragWithFriction({
          freeDistance: freeDistance.get(),
          maxDistance: freeDistance.get() + HORIZONTAL_DRAG_OVERSHOOT,
          translation: startOffset.get() + event.translationX,
        });
        dragX.set(shown);
        tilt.set(tiltForHorizontalOffset(shown, cardWidth.get()));
      })
      .onEnd((event, success) => {
        if (!success || !hasDragged.get() || !ownsTouch() || exitingOperation.get() !== null) {
          return;
        }
        // RNGH also calls onEnd(false) for an active pan that fails or is cancelled.
        // Finalize owns that recovery; only a successful release may record an action.
        isDragging.set(false);
        if (dragTarget.get() === DRAG_TARGET_VIEW) {
          if (views === null) {
            return;
          }
          // The release a card would commit sends the view off that side instead.
          const releaseOffset = startOffset.get() + event.translationX;
          if (
            views.incoming.get() !== views.shown.get() &&
            isHorizontalDragCommitted({
              freeDistance: freeDistance.get(),
              translationX: releaseOffset,
            })
          ) {
            exitSlideView(views, releaseOffset < 0 ? -1 : 1);
            runOnJS(swipeToPage)(views.incoming.get());
          } else {
            restView();
          }
          if (hasReportedDragStart.get()) {
            runOnJS(cancel)(activeOwner.get());
          }
          return;
        }
        const owner = activeOwner.get();
        const isReactCurrent = owner === presentationKey;
        if (isOwnerCommitted(owner)) {
          snapToEdge(event.translationX < 0 ? "left" : "right", owner);
          return;
        }
        // A slide card's release is where it paged to: only ever to the left.
        const releaseOffset =
          dragTarget.get() === DRAG_TARGET_PAGE
            ? dragX.get()
            : startOffset.get() + event.translationX;
        if (
          isHorizontalDragCommitted({
            freeDistance: freeDistance.get(),
            translationX: releaseOffset,
          })
        ) {
          const direction = releaseOffset < 0 ? "left" : "right";
          if (isReactCurrent && isSwipeBack) {
            dragX.set(withSpring(0, LEARN_DECK_RECOVERY_SPRING));
            tilt.set(withSpring(0, LEARN_DECK_RECOVERY_SPRING));
            runOnJS(swipeBack)();
            return;
          }
          const id = gestureId.get();
          // Acknowledged on the UI thread, so a busy JS thread never leaves the released card
          // frozen at the finger's last position. The controller still owns validation,
          // persistence and recovery. A card React has not promoted yet (or whose predecessor is
          // still waiting for its exit) parks until it can commit.
          const mode = isReactCurrent && release.get() === null ? releaseMode.get() : "park";
          if (mode === "fly") {
            releaseLearnCard(stage, owner, direction);
          } else if (mode === "return") {
            springHome();
          } else {
            snapToEdge(direction, owner, false);
          }
          runOnJS(commit)(direction, owner, id);
          return;
        }
        springHome();
        runOnJS(cancel)(owner);
      })
      .onFinalize((_event, success) => {
        if (!ownsTouch()) {
          return;
        }
        isDragging.set(false);
        if (success || !hasDragged.get() || exitingOperation.get() !== null) {
          return;
        }
        hasDragged.set(false);
        const owner = activeOwner.get();
        if (dragTarget.get() === DRAG_TARGET_VIEW) {
          // An interrupted view drag was never released: the view returns home.
          restView();
          if (hasReportedDragStart.get()) {
            runOnJS(cancel)(owner);
          }
          return;
        }
        if (isOwnerCommitted(owner)) {
          snapToEdge(dragX.get() < 0 ? "left" : "right", owner);
          return;
        }
        // Interrupted by another recogniser (or the touch was cancelled): the card was never
        // released, so it returns home, keeping any revealed answer, without rating.
        springHome();
        runOnJS(cancel)(owner);
      });
    if (allowVerticalScroll) {
      gesture.failOffsetY([-VERTICAL_FAIL_PX, VERTICAL_FAIL_PX]);
    }
    return { gesture, interrupt };
  }, [
    acceptsTouches,
    activeGestureId,
    activeOwner,
    allowVerticalScroll,
    blockerViews,
    dragTarget,
    freeDistance,
    hasDragged,
    hasReportedDragStart,
    isAppActive,
    isCommitted,
    isEnabled,
    isSwipeBack,
    onCommitRef,
    onDragCancelRef,
    onDragStartRef,
    onParkRef,
    onSwipeBackRef,
    onSwipeToPageRef,
    pageCount,
    pageGrabX,
    presentationKey,
    revealOnDragStart,
    stage,
    startOffset,
    views,
  ]);

  useEffect(() => {
    runOnUI(() => {
      acceptsTouches.set(isEnabled && isAppActive);
      if (!isEnabled || !isAppActive) {
        interrupt();
      }
    })();
  }, [acceptsTouches, interrupt, isAppActive, isEnabled]);

  const interruptRef = useLatest(interrupt);
  const enabledRef = useLatest(isEnabled);
  useEffect(() => {
    const updatePresence = (active: boolean) => {
      const recover = interruptRef.current;
      const enabled = enabledRef.current;
      // Recover on return too: background execution can be suspended before cleanup runs.
      runOnUI(() => {
        acceptsTouches.set(false);
        recover();
        acceptsTouches.set(active && enabled);
      })();
      setIsAppActive(active);
    };
    const change = AppState.addEventListener("change", (state) =>
      updatePresence(state === "active"),
    );
    // Android-only: iOS's RCTAppState has no `appStateFocusChange` event (adding the
    // listener throws), and react-native-web only supports `change`.
    const blur =
      Platform.OS === "android"
        ? AppState.addEventListener("blur", () => updatePresence(false))
        : undefined;
    const focus =
      Platform.OS === "android"
        ? AppState.addEventListener("focus", () =>
            updatePresence(AppState.currentState === "active"),
          )
        : undefined;
    return () => {
      change.remove();
      blur?.remove();
      focus?.remove();
    };
  }, [acceptsTouches, enabledRef, interruptRef]);

  return gesture;
}
